import { TypedDocumentNode, gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';

import { OnboardingStepDialog } from '@sorare/core/src/components/rivals/OnboardingStepDialog';
import { FOOTBALL_PLAY_RIVALS_DRAFT_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { rivals } from '@sorare/core/src/lib/glossary';

import { useFootballRivalsUpdateFavoriteTeam } from 'components/rivals/Onboarding/FavoriteClubSelection/useFootballRivalsUpdateFavoriteTeam';
import { useRivalsOnboarding } from 'components/rivals/Onboarding/useRivalsOnboarding';

import { LearnTheBasics } from './LearnTheBasics';
import { WelcomeToSorare } from './WelcomeToSorare';
import {
  ClubSelectionDialogQuery,
  ClubSelectionDialogQueryVariables,
} from './__generated__/index.graphql';

const CLUB_SELECTION_DIALOG_QUERY = gql`
  query ClubSelectionDialogQuery($clubSlug: String!) {
    football {
      club(slug: $clubSlug) {
        slug
        id
        styleGuideColor
        lastRivalsGame {
          slug
          id
          game {
            id
            date
            competition {
              slug
              name
            }
            homeTeam {
              slug
              id
            }
            awayTeam {
              slug
              id
            }
          }
          ...LearnTheBasics_footballRivalsGame
        }
        ...WelcomeToSorare_club
      }
    }
  }
  ${WelcomeToSorare.fragments.club}
  ${LearnTheBasics.fragments.footballRivalsGame}
` as TypedDocumentNode<
  ClubSelectionDialogQuery,
  ClubSelectionDialogQueryVariables
>;

type Props = { clubSlug: string | null; open?: boolean; onClose: () => void };
export const ClubSelectionDialog = ({ clubSlug, open, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { markOnboardingAsDone } = useRivalsOnboarding();
  const [mutate] = useFootballRivalsUpdateFavoriteTeam();

  const { data, previousData, loading } = useQuery(
    CLUB_SELECTION_DIALOG_QUERY,
    {
      variables: { clubSlug: clubSlug! },
      skip: !clubSlug,
    }
  );

  const fetchedData = data || previousData;

  if (loading) {
    return null;
  }

  const { club } = fetchedData?.football || {};
  const { lastRivalsGame } = club || {};

  const onDone = () => {
    if (club?.id) {
      mutate({ variables: { input: { teamId: club.id } } });
    }

    navigate(
      `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, { slug: lastRivalsGame?.slug || '' })}?simulation=true`
    );
  };
  const onSkip = () => {
    if (club?.id) {
      mutate({ variables: { input: { teamId: club.id } } });
    }
    markOnboardingAsDone();
  };

  return (
    <OnboardingStepDialog
      open={!!open}
      onDone={onDone}
      onSkip={onSkip}
      onClose={onClose}
      lastNextCtaLabel={formatMessage(rivals.setLineup)}
    >
      <WelcomeToSorare club={club} />
      <LearnTheBasics
        color={club?.styleGuideColor}
        rivalsGame={lastRivalsGame}
      />
    </OnboardingStepDialog>
  );
};
